import * as React from 'react';
import { useState, useEffect } from 'react';
import { QuickTagCreationWizardPageProps } from '../QuickTagCreationWizard';
import Switch from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import Select from 'react-select';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dropzone from 'react-dropzone';
import ReactTooltip from 'react-tooltip';
import Loader from '../../../common/Loader/Loader';
import * as Cookies from 'js-cookie';
import config from '../../../../config';
import http from '../../../../utils/http';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';

enum UPLOAD_STATUS {
    WAITING = 'WAITING',
    UPLOADING = 'UPLOADING',
    VALID = 'VALID'
}

const CODEFRAME_EXAMPLE_FILE_URL = `${STATIC_STORAGE_URL}/doc/codeframe_example.csv`;

const GENERATE_OPTIONS: { value: boolean; label: string; subtitle: string }[] =
    [
        {
            value: true,
            label: 'Use an AI generated CodeFrame',
            subtitle:
                'Use a dynamically created CodeFrame based on the themes identified from the verbatims'
        },
        {
            value: false,
            label: 'Use an existing CodeFrame',
            subtitle: ''
        }
    ];

/**
 * QuickTagCreationWizard Page for Codeframe Generation.
 */
function QuickTagCodeframe(props: QuickTagCreationWizardPageProps) {
    const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.WAITING);
    const [parsedCodeFrameFields, setParsedCodeFrameFields] = useState([]);
    const [
        selectedExistingCodeFrameLabelSelectValue,
        setSelectedExistingCodeFrameLabelSelectValue
    ] = useState<{ value: string; label: string } | null>();
    useEffect(() => {
        setSelectedExistingCodeFrameLabelSelectValue({
            value: props.selectedExistingCodeFrameLabel,
            label: props.selectedExistingCodeFrameLabel
        });
    }, [props.selectedExistingCodeFrameLabel]);
    const handleSelectExistingCodeFrame = (codeFrameLabel: string) => {
        props.setSelectedExistingCodeFrameLabel(codeFrameLabel);
    };
    const handleUploadError = (error: string) => {
        setUploadStatus(UPLOAD_STATUS.WAITING);
        alert(error);
        console.error(error);
    };

    async function handleQuickTagCodeFrameCreate(
        acceptedFiles: any
    ): Promise<void> {
        const file = acceptedFiles[0];
        const cssToken = Cookies.get('csst');
        if (cssToken) {
            try {
                setUploadStatus(UPLOAD_STATUS.UPLOADING);
                const formData = new FormData();
                formData.append('file', file);
                const url =
                    config.api.endpoints.createQuickTagCodeFrameFromFile;
                const response = await http.post(url, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response?.status !== 200) {
                    handleUploadError(
                        `CodeFrame import failed with error status: ${response?.status}`
                    );
                } else {
                    setUploadStatus(UPLOAD_STATUS.VALID);
                    setParsedCodeFrameFields(
                        response.data.parsedCodeFrameFields
                    );
                    props.setSelectedExistingCodeFrameLabel(
                        response.data.codeFrameLabel
                    );
                }
            } catch (e) {
                if (e.response) handleUploadError(e.response.data.message);
                else handleUploadError(e);
            }
        }
    }
    const existingCodeFrameSelectObjs = props.codeFrameLabels.map((label) => ({
        value: label,
        label: label
    }));
    const dropzoneInnerContent =
        uploadStatus === UPLOAD_STATUS.UPLOADING ? (
            <Loader />
        ) : uploadStatus === UPLOAD_STATUS.VALID ? (
            <>
                <i className="nx nx-check text-5xl" />
                <br />
                <span>
                    Looking good!
                    <br />
                    CodeFrame created with the following fields:{' '}
                    {parsedCodeFrameFields.join(', ')}
                </span>
            </>
        ) : (
            <>
                <i className="nx nx-upload text-5xl" />
                <br />
                <span>Drop your file here or click to browse.</span>
            </>
        );
    return (
        <>
            <FormControl component="fieldset" className="w-full">
                <RadioGroup
                    name="useAiGeneratedCodeFrame"
                    value={
                        GENERATE_OPTIONS.find(
                            (opt) => opt.value == props.useAiGeneratedCodeFrame
                        ).label
                    }
                    onChange={(e) => {
                        console.log(e.target);
                        const selectedOption = GENERATE_OPTIONS.find(
                            (opt) => opt.label == e.target.value
                        );

                        props.setUseAiGeneratedCodeFrame(selectedOption.value);
                    }}
                >
                    {GENERATE_OPTIONS.map((opt) => (
                        <FormControlLabel
                            key={opt.label}
                            value={opt.label}
                            control={
                                <>
                                    <Radio color="primary" value={opt.label} />
                                    <div>
                                        <div>{opt.label}</div>
                                        <div className="text-gray-500 text-xs">
                                            {opt.subtitle}
                                        </div>
                                    </div>
                                </>
                            }
                            label={''}
                        />
                    ))}
                </RadioGroup>
            </FormControl>
            {!props.useAiGeneratedCodeFrame ? (
                <div className="flex flex-col pl-8 -mt-2">
                    <label className="text-xs mb-1">
                        Select an existing CodeFrame:
                    </label>
                    <Select
                        className="mb-2"
                        size="small"
                        autoFocus={false}
                        key="existing-codeFrame-select"
                        options={existingCodeFrameSelectObjs}
                        value={selectedExistingCodeFrameLabelSelectValue}
                        noOptionsMessage={() => 'No existing CodeFrames found.'}
                        onChange={(selectedOption) =>
                            handleSelectExistingCodeFrame(selectedOption.label)
                        }
                    />
                    <span>
                        or{' '}
                        <a
                            data-tip="quickTag-wizard-tooltip-codeframe-file"
                            data-for="quickTag-wizard-tooltip-codeframe-file"
                            className="quickTag-wizard-tooltip"
                        >
                            import a CodeFrame
                        </a>{' '}
                        below...
                    </span>
                    <ReactTooltip
                        id="quickTag-wizard-tooltip-codeframe-file"
                        place="top"
                        clickable={true}
                        delayHide={250}
                        getContent={function () {
                            return (
                                <span>
                                    A CodeFrame file is a CSV (.csv) file with a
                                    column labelled <b>CodeLabel</b> containing
                                    a list of code labels.
                                    <br />
                                    It may also include the following optional
                                    columns:
                                    <ul>
                                        <li>
                                            <b>CodeID</b> (unique identifier)
                                        </li>
                                        <li>
                                            <b>CodeSentiment</b>{' '}
                                            (&quot;Positive&quot;,
                                            &quot;Negative&quot;, or
                                            &quot;Neutral&quot;)
                                        </li>
                                        <li>
                                            <b>CodeNet</b> (label of net
                                            category)
                                        </li>
                                        <li>
                                            <b>Example 1</b> (example verbatim)
                                        </li>
                                        <li>
                                            <b>Example 2</b> (example verbatim)
                                        </li>
                                        <li>
                                            <b>Example 3</b> (example verbatim)
                                        </li>
                                    </ul>
                                    The filename of the uploaded file will be
                                    used as the CodeFrame label.
                                    <br />
                                    Download{' '}
                                    <a
                                        href={CODEFRAME_EXAMPLE_FILE_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        codeframe_example.csv
                                    </a>{' '}
                                    to see an example codeframe file.
                                </span>
                            );
                        }}
                    />
                    <Dropzone
                        accept={{ 'text/csv': ['.csv'] }}
                        multiple={false}
                        onDrop={async (file) =>
                            await handleQuickTagCodeFrameCreate(file)
                        }
                    >
                        {({ getRootProps, getInputProps }: any) => (
                            <div
                                className="text-blue text-sm text-center cursor-pointer"
                                {...getRootProps()}
                            >
                                <input {...getInputProps()} />
                                <div>{dropzoneInnerContent}</div>
                            </div>
                        )}
                    </Dropzone>
                </div>
            ) : null}
        </>
    );
}

export default QuickTagCodeframe;
